import React, { useEffect, useState } from "react";
import Dialog from "../../../../common/element/Dialog/Dialog";
import Tag from "../../../../common/element/Tag";
import { useDispatch } from "react-redux";
import { GetSignedUrlByKey } from "../../../../actions/public/GetSignedUrlByKey";
import { GetProviderInvoicesById } from "../../../../actions/provider-portal/invoices/GetProviderInvoicesById";
import moment from "moment";

const InvoiceDetailsModal = ({ isOpen, onClose, invoice, invoiceStatus }) => {
  const dispatch = useDispatch();
  const [details, setDetails] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (isOpen && invoice) {
      setLoading(true);

      const rawPdf = invoice?.xero_invoice_pdf;
      if (rawPdf && (rawPdf.startsWith("http://") || rawPdf.startsWith("https://"))) {
        setPdfUrl(rawPdf);
      } else if (rawPdf) {
        dispatch(GetSignedUrlByKey({ url: rawPdf }, "provider"))
          .then((res) => {
            if (res?.success && res?.data) {
              setPdfUrl(res.data);
            }
          })
          .catch(() => {});
      }

      if (invoice?.sub_invoice_id) {
        dispatch(GetProviderInvoicesById({ sub_invoice_id: invoice.sub_invoice_id }))
          .then((response) => {
            setLoading(false);
            if (response?.success && response?.data) {
              setDetails(response.data);
              if (response.data.xero_invoice_pdf?.startsWith("http")) {
                setPdfUrl(response.data.xero_invoice_pdf);
              }
            } else {
              setDetails(invoice);
            }
          })
          .catch(() => {
            setLoading(false);
            setDetails(invoice);
          });
      } else {
        setLoading(false);
        setDetails(invoice);
      }
    } else {
      setPdfUrl(null);
      setDetails(null);
      setLoading(false);
    }
  }, [isOpen, invoice, dispatch]);

  if (!isOpen || !invoice) return null;

  const currentData = details || invoice;
  const isUpcoming = invoiceStatus === "UPCOMING" || currentData?.status === "upcoming";
  const isPaid = invoiceStatus === "PAID" || currentData?.status === "paid" || currentData?.xero_invoice_status === "PAID";
  const isOverdue = invoiceStatus === "OVERDUE";
  const isDraft = currentData?.status === "draft" || currentData?.xero_invoice_status === "DRAFT";

  const handleDownload = async () => {
    if (!pdfUrl) return;
    try {
      setDownloadingPdf(true);
      const res = await fetch(pdfUrl);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${currentData?.xero_invoice_number || "Invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadingPdf(false);
    } catch (e) {
      window.open(pdfUrl, "_blank");
      setDownloadingPdf(false);
    }
  };

  const invoiceNumber = currentData?.xero_invoice_number || `INV-${currentData?.sub_invoice_id || ""}`;
  const totalAmount = parseFloat(currentData?.amount || 0).toFixed(2);
  const subtotal = (parseFloat(totalAmount) / 1.1).toFixed(2);
  const taxAmount = (parseFloat(totalAmount) - parseFloat(subtotal)).toFixed(2);
  const providerName = currentData?.invoiceProv?.trading_name || currentData?.trading_name || "Provider";
  const invoiceDate = currentData?.invoice_send_date
    ? moment(currentData?.invoice_send_date).format("DD MMM YYYY")
    : currentData?.created_date
    ? moment(currentData?.created_date).format("DD MMM YYYY")
    : moment().format("DD MMM YYYY");
  const dueDate = currentData?.due_date
    ? moment(currentData?.due_date).format("DD MMM YYYY")
    : "-";

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      onRequestClose={onClose}
      width={940}
      className="invoice-document-dialog"
      contentClassName="p-0 overflow-hidden rounded-2xl bg-white shadow-2xl border border-gray-200"
    >
      <div className="flex flex-col h-[90vh] max-h-[900px] w-full bg-white">
        {/* Top Action Header Bar */}
        <div className="px-6 py-3.5 flex items-center justify-between border-b border-gray-200 bg-white z-10 shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900 tracking-tight">
              {invoiceNumber}
            </h2>
            {isPaid && (
              <Tag className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1.5 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Paid
              </Tag>
            )}
            {isUpcoming && (
              <Tag className="bg-amber-50 text-amber-800 border border-amber-200 font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1.5 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                Upcoming Renewal
              </Tag>
            )}
            {isOverdue && (
              <Tag className="bg-rose-50 text-rose-700 border border-rose-200 font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1.5 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                Overdue
              </Tag>
            )}
            {isDraft && (
              <Tag className="bg-gray-100 text-gray-700 border border-gray-200 font-semibold px-2.5 py-0.5 rounded-full text-xs">
                Draft
              </Tag>
            )}
            {!isPaid && !isUpcoming && !isOverdue && !isDraft && (
              <Tag className="bg-blue-50 text-blue-700 border border-blue-200 font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1.5 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                Due
              </Tag>
            )}
          </div>

          <div className="flex items-center gap-3">
            {pdfUrl && (
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloadingPdf}
                className="px-3.5 py-2 text-xs font-medium rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                title="Download PDF"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 10v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2M11 6.5L8 9.5M8 9.5L5 6.5M8 9.5V1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{downloadingPdf ? "Downloading..." : "Download PDF"}</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              title="Close"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Main Document Body */}
        <div className="flex-1 w-full bg-[#525659] overflow-hidden relative">
          {pdfUrl ? (
            <object
              data={`${pdfUrl}#toolbar=0&navpanes=0`}
              type="application/pdf"
              className="w-full h-full border-0"
            >
              <iframe
                src={`${pdfUrl}#toolbar=0&navpanes=0`}
                className="w-full h-full border-0"
                title={invoiceNumber}
              />
            </object>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center h-full text-white">
              <div className="w-10 h-10 border-4 border-gray-400 border-t-white rounded-full animate-spin mb-3"></div>
              <p className="text-sm font-medium text-gray-200">Loading invoice PDF...</p>
            </div>
          ) : (
            /* Fallback HTML Document formatted exactly like the Xero PDF */
            <div className="h-full overflow-y-auto p-8 flex justify-center">
              <div className="bg-white text-gray-900 w-full max-w-[800px] min-h-[900px] p-12 shadow-2xl rounded-sm font-sans flex flex-col justify-between">
                <div>
                  {/* PDF Header: Title + Logo */}
                  <div className="flex justify-between items-start">
                    <div>
                      <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 uppercase">
                        INVOICE
                      </h1>
                      <p className="text-base font-semibold text-gray-800 mt-4">
                        {providerName}
                      </p>
                    </div>

                    <div className="text-right">
                      <div
                        className="text-4xl font-extrabold tracking-wider"
                        style={{
                          fontFamily: "cursive, Georgia, serif",
                          color: "#F59E0B"
                        }}
                      >
                        Demo Co.
                      </div>
                    </div>
                  </div>

                  {/* Metadata and Company Address */}
                  <div className="flex justify-between items-start mt-8 pt-4">
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="font-bold text-gray-900 block">Invoice Date</span>
                        <span className="text-gray-700">{invoiceDate}</span>
                      </div>
                      <div>
                        <span className="font-bold text-gray-900 block">Invoice Number</span>
                        <span className="text-gray-700">{invoiceNumber}</span>
                      </div>
                      <div>
                        <span className="font-bold text-gray-900 block">Reference</span>
                        <span className="text-gray-700">Invoice Reference</span>
                      </div>
                      <div>
                        <span className="font-bold text-gray-900 block">Tax reg</span>
                        <span className="text-gray-700">101-2-303</span>
                      </div>
                    </div>

                    <div className="text-right text-sm text-gray-700 space-y-1">
                      <div className="font-bold text-gray-900">Demo Company (Global)</div>
                      <div>23 Main Street</div>
                      <div>Central City</div>
                      <div>MARINEVILLE 12345</div>
                    </div>
                  </div>

                  {/* Line Items Table */}
                  <div className="mt-12">
                    <div className="grid grid-cols-12 text-sm font-bold text-gray-900 pb-2 border-b-2 border-gray-900">
                      <div className="col-span-6">Description</div>
                      <div className="col-span-2 text-center">Quantity</div>
                      <div className="col-span-2 text-right">Unit Price</div>
                      <div className="col-span-1 text-right">Tax</div>
                      <div className="col-span-1 text-right">Amount</div>
                    </div>

                    <div className="grid grid-cols-12 text-sm text-gray-800 py-3 border-b border-gray-200">
                      <div className="col-span-6 font-medium">
                        Subscription Invoice - {providerName}
                      </div>
                      <div className="col-span-2 text-center">1.00</div>
                      <div className="col-span-2 text-right">{subtotal}</div>
                      <div className="col-span-1 text-right">10%</div>
                      <div className="col-span-1 text-right">{subtotal}</div>
                    </div>

                    {/* Totals Section */}
                    <div className="flex justify-end mt-4">
                      <div className="w-72 space-y-2 text-sm">
                        <div className="flex justify-between text-gray-800">
                          <span className="font-medium">Subtotal</span>
                          <span>{subtotal}</span>
                        </div>
                        <div className="flex justify-between text-gray-800">
                          <span className="font-medium uppercase">TOTAL GST 10%</span>
                          <span>{taxAmount}</span>
                        </div>
                        <div className="border-t-2 border-gray-900 pt-2 flex justify-between font-bold text-base text-gray-900">
                          <span>TOTAL</span>
                          <span>{totalAmount}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Due Date at Bottom */}
                <div className="mt-16 pt-4 text-sm font-bold text-gray-900">
                  Due Date: {dueDate}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default InvoiceDetailsModal;
